// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AttachableCharge } from '@/billing/api/get-attachable-charges';
import { InvoiceLineItem } from '@/billing/api/types';
import {
  NewLineItemEntry,
  UpdateLineItemEntry,
} from '@/billing/api/update-line-items';
import {
  NewChargeFormData,
  quantityInputSchema,
} from '@/billing/schemas/new-charge-form-schema';
import { pricingModeSchema } from '@/constants/pricing-mode';
import {
  Currency,
  SerializableCurrency,
  refineCurrencyAmountString,
} from '@/currency/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { MAX_DOLLAR_VALUE, MAX_DOLLAR_VALUE_LABEL } from '@/utilities/currency';
import { serializeDateForAPI } from '@/utilities/date-time';
import { dateTimeOrNullSchema } from '@/utilities/date-time-schema';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

// Tuple [start, end]. superRefine enforces the valid-time-vector rule: both
// bounds must be set together (or both cleared), and if both are present the
// end must not precede the start. Issues attach to the tuple root (no path
// override) so react-hook-form surfaces the message on the servicePeriod
// field — FormDateRangePicker reads fieldState.error?.message at that level
// to render the helperText, and nested paths like [0]/[1] wouldn't reach it.
const servicePeriodSchema = z
  .tuple([dateTimeOrNullSchema, dateTimeOrNullSchema])
  .superRefine(([start, end], ctx) => {
    if (start !== null && end === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date is required when start is set',
      });
      return;
    }
    if (start === null && end !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start date is required when end is set',
      });
      return;
    }
    if (start !== null && end !== null && end < start) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be on or after the start date',
      });
    }
  });

// Form-state shape of an amount input bound to FormCurrencyTextField:
// a bare-string amount carried alongside the line item's locked currency.
// The amount stays a string (not a Decimal) so the object-level refines and
// the live-totals watcher can read it directly, and an empty amount means
// "no value". The currency comes from the invoice and is locked in the UI.
const lineItemCurrencyAmountSchema = z
  .object({
    amount: z.string(),
    isoCode: z.preprocess(
      (value) => (typeof value === 'string' ? value.toUpperCase() : value),
      isoCurrencyCodeSchema
    ),
  })
  .superRefine(refineCurrencyAmountString);

// Marks a row as a queued ad-hoc create. Snapshot fields the wire payload
// and the row UI both read.
const adhocSpecSchema = z.object({
  itemKey: z.string(),
  itemName: z.string(),
  itemSku: z.string(),
  locationKey: z.string().nullable(),
  expectedBillableDate: dateTimeOrNullSchema,
});

// A row is exactly one of: existing (key), queued attach (pendingChargeKey),
// or queued ad-hoc (adhocSpec).
const lineItemFormValuesSchema = z
  .object({
    key: z.string().nullable(),
    pendingChargeKey: z.string().nullable(),
    adhocSpec: adhocSpecSchema.nullable(),
    billingEligibleDate: dateTimeOrNullSchema,
    description: z.string(),
    pricingMode: pricingModeSchema,
    quantity: quantityInputSchema.refine((value) => value !== '', {
      message: 'Quantity is required',
    }),
    unitPrice: lineItemCurrencyAmountSchema,
    totalPrice: lineItemCurrencyAmountSchema,
    taxAmount: lineItemCurrencyAmountSchema,
    servicePeriod: servicePeriodSchema,
  })
  .refine(
    (value) => {
      const setCount =
        (value.key !== null ? 1 : 0) +
        (value.pendingChargeKey !== null ? 1 : 0) +
        (value.adhocSpec !== null ? 1 : 0);
      return setCount === 1;
    },
    {
      message:
        'line item must have exactly one of key, pendingChargeKey, or adhocSpec',
    }
  )
  .refine(
    (value) =>
      value.pendingChargeKey === null || value.billingEligibleDate !== null,
    {
      message: 'billingEligibleDate is required for queued additions',
      path: ['billingEligibleDate'],
    }
  )
  // The total is required, and in PER_UNIT mode it comes from the unit price,
  // so a missing total is flagged on whichever input the row shows.
  .refine(
    (value) => value.pricingMode !== 'FIXED' || value.totalPrice.amount !== '',
    { message: 'Total price is required', path: ['totalPrice', 'amount'] }
  )
  .refine(
    (value) =>
      value.pricingMode !== 'PER_UNIT' || value.unitPrice.amount !== '',
    { message: 'Unit price is required', path: ['unitPrice', 'amount'] }
  )
  // Bound checks live at the object level because totalPrice's visible
  // input depends on pricingMode — PER_UNIT shows unit price, FIXED shows
  // total. A field-level bound on totalPrice would short-circuit object
  // parsing (Zod v3 only runs object refines after fields pass) and
  // attach the message to an invisible input in PER_UNIT mode.
  .refine(
    (value) => {
      if (value.pricingMode !== 'PER_UNIT' || value.unitPrice.amount === '') {
        return true;
      }
      const qty = Number(value.quantity) || 0;
      const price = Number(value.unitPrice.amount) || 0;
      return qty * price <= MAX_DOLLAR_VALUE;
    },
    {
      message: `Line total must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      path: ['unitPrice', 'amount'],
    }
  )
  .refine(
    (value) => {
      if (value.pricingMode !== 'FIXED' || value.totalPrice.amount === '') {
        return true;
      }
      return Number(value.totalPrice.amount) <= MAX_DOLLAR_VALUE;
    },
    {
      message: `Line total must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      path: ['totalPrice', 'amount'],
    }
  )
  .refine(
    (value) =>
      value.taxAmount.amount === '' ||
      Number(value.taxAmount.amount) <= MAX_DOLLAR_VALUE,
    {
      message: `Dollar value must not exceed ${MAX_DOLLAR_VALUE_LABEL}`,
      path: ['taxAmount', 'amount'],
    }
  );

export const lineItemsFormSchema = z.object({
  lineItems: z.array(lineItemFormValuesSchema),
});

type LineItemFormValues = z.infer<typeof lineItemFormValuesSchema>;
export type LineItemsFormState = z.infer<typeof lineItemsFormSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const compactInputSx = { fontSize: '0.8125rem' } as const;

// Wraps a bare form amount in the currency-object shape FormCurrencyTextField
// binds. An empty amount means "no value"; the currency is the invoice's,
// locked in the UI.
function toFormAmount(
  amount: string,
  currencyCode: IsoCurrencyCode
): SerializableCurrency {
  return { amount, isoCode: currencyCode };
}

export function toFormData(
  li: InvoiceLineItem,
  currencyCode: IsoCurrencyCode
): LineItemFormValues {
  return {
    key: li.key,
    pendingChargeKey: null,
    adhocSpec: null,
    billingEligibleDate: null,
    description: li.description,
    pricingMode: li.pricingMode,
    quantity: String(li.quantity),
    unitPrice: toFormAmount(
      li.unitPrice !== null ? li.unitPrice.amount.toString() : '',
      currencyCode
    ),
    totalPrice: toFormAmount(li.totalPrice.amount.toString(), currencyCode),
    taxAmount: toFormAmount(
      li.taxAmount !== null ? li.taxAmount.amount.toString() : '',
      currencyCode
    ),
    servicePeriod: [li.servicePeriodStart, li.servicePeriodEnd],
  };
}

// Seeds the form-state row for a charge the user added inside the edit
// queue. Snapshot fields come straight from the picker option so the user
// sees the same numbers the BE will create.
export function queuedAttachToFormData(
  charge: AttachableCharge,
  billingEligibleDate: DateTime,
  currencyCode: IsoCurrencyCode
): LineItemFormValues {
  return {
    key: null,
    pendingChargeKey: charge.chargeKey,
    adhocSpec: null,
    billingEligibleDate,
    description: charge.label,
    pricingMode: charge.pricingMode,
    quantity: String(charge.quantity),
    unitPrice: toFormAmount(
      charge.unitPrice !== null
        ? toSerializableCurrency(charge.unitPrice).amount
        : '',
      currencyCode
    ),
    totalPrice: toFormAmount(
      toSerializableCurrency(charge.totalPrice).amount,
      currencyCode
    ),
    taxAmount: toFormAmount('', currencyCode),
    servicePeriod: [charge.servicePeriodStart, charge.servicePeriodEnd],
  };
}

// Multiplies a validated quantity and unit price exactly, padded to the
// currency's minor unit.
function toPerUnitTotalAmount(
  quantity: string,
  unitPriceAmount: string,
  currencyCode: IsoCurrencyCode
): string {
  const parsedQuantity = parseCurrencyAmount(quantity);
  const parsedUnitPrice = parseCurrencyAmount(unitPriceAmount);
  if (parsedQuantity === null || parsedUnitPrice === null) {
    throw new Error(
      `toPerUnitTotalAmount: unparseable quantity "${quantity}" or unit price "${unitPriceAmount}"`
    );
  }

  return padAmountToMinorUnit(
    parsedQuantity.times(parsedUnitPrice),
    currencyCode
  );
}

// Seeds the form-state row for an ad-hoc charge from the new-charge form.
// PER_UNIT mode derives totalPrice from quantity × unitPrice at queue time
// (the form never binds totalPrice in that mode).
export function queuedAdhocToFormData(
  data: NewChargeFormData,
  currencyCode: IsoCurrencyCode
): LineItemFormValues {
  if (data.item === null) {
    throw new Error(
      'queuedAdhocToFormData: data.item is null after schema validation'
    );
  }
  const totalPrice =
    data.pricingMode === 'PER_UNIT'
      ? toPerUnitTotalAmount(data.quantity, data.unitPrice.amount, currencyCode)
      : data.totalPrice.amount;
  return {
    key: null,
    pendingChargeKey: null,
    adhocSpec: {
      itemKey: data.item.key,
      itemName: data.item.name,
      itemSku: data.item.sku,
      locationKey: data.location?.key ?? null,
      expectedBillableDate: data.expectedBillableDate,
    },
    billingEligibleDate: null,
    description: data.description.trim(),
    pricingMode: data.pricingMode,
    quantity: data.quantity,
    unitPrice: toFormAmount(data.unitPrice.amount, currencyCode),
    totalPrice: toFormAmount(totalPrice, currencyCode),
    taxAmount: toFormAmount(data.taxAmount.amount, currencyCode),
    servicePeriod: data.servicePeriod,
  };
}

export function toUpdateEntry(
  li: LineItemFormValues,
  sequence: number
): UpdateLineItemEntry {
  const [start, end] = li.servicePeriod;
  return {
    key: li.key,
    pendingChargeKey: li.pendingChargeKey,
    billingEligibleDate:
      li.billingEligibleDate !== null
        ? serializeDateForAPI(li.billingEligibleDate)
        : null,
    description: li.description,
    pricingMode: li.pricingMode,
    quantity: Number(li.quantity),
    unitPrice:
      li.unitPrice.amount !== ''
        ? toFormAmountCurrency(li.unitPrice.amount, li.unitPrice.isoCode)
        : null,
    totalPrice: toFormAmountCurrency(
      li.totalPrice.amount,
      li.totalPrice.isoCode
    ),
    taxAmount:
      li.taxAmount.amount !== ''
        ? toFormAmountCurrency(li.taxAmount.amount, li.taxAmount.isoCode)
        : null,
    servicePeriodStart: start ? serializeDateForAPI(start) : null,
    servicePeriodEnd: end ? serializeDateForAPI(end) : null,
    sequence,
  };
}

// Parses a bare form amount into a Currency exactly, never rounded, so an
// over-precise amount reaches the backend's rejection unchanged.
function toFormAmountCurrency(
  amount: string,
  currencyCode: IsoCurrencyCode
): Currency {
  const parsed = parseCurrencyAmount(amount);
  if (parsed === null) {
    throw new Error(`toFormAmountCurrency: unparseable amount "${amount}"`);
  }

  return {
    amount: parsed,
    isoCode: currencyCode,
  };
}

export function toNewLineItemEntry(
  li: LineItemFormValues,
  sequence: number,
  currencyCode: IsoCurrencyCode
): NewLineItemEntry {
  if (li.adhocSpec === null) {
    throw new Error('toNewLineItemEntry called on a row without adhocSpec');
  }
  const [start, end] = li.servicePeriod;
  return {
    itemKey: li.adhocSpec.itemKey,
    locationKey: li.adhocSpec.locationKey,
    chargeLabel: li.description,
    pricingMode: li.pricingMode,
    quantity: Number(li.quantity),
    unitPrice:
      li.unitPrice.amount !== ''
        ? toFormAmountCurrency(li.unitPrice.amount, currencyCode)
        : null,
    totalPrice: toFormAmountCurrency(li.totalPrice.amount, currencyCode),
    taxAmount:
      li.taxAmount.amount !== ''
        ? toFormAmountCurrency(li.taxAmount.amount, currencyCode)
        : null,
    servicePeriodStart: start ? serializeDateForAPI(start) : null,
    servicePeriodEnd: end ? serializeDateForAPI(end) : null,
    expectedBillableDate:
      li.adhocSpec.expectedBillableDate !== null
        ? serializeDateForAPI(li.adhocSpec.expectedBillableDate)
        : null,
    sequence,
  };
}

/** Parses a form amount string; blank or partial input is zero. */
export function parseFormAmount(amount: string | null | undefined): Decimal {
  return parseCurrencyAmount(amount ?? '') ?? new CurrencyDecimal(0);
}

export function computeLineTotal(
  totalPrice: string | null,
  taxAmount: string | null
): Decimal {
  return parseFormAmount(totalPrice).plus(parseFormAmount(taxAmount));
}

// ---------------------------------------------------------------------------
// Drag-and-drop data helpers
// ---------------------------------------------------------------------------

const LINE_ITEM_DND_TYPE = 'editable-line-item';

export function makeLineItemDragData(
  index: number,
  key: string | null
): Record<string, unknown> {
  return { type: LINE_ITEM_DND_TYPE, index, key };
}

export function isLineItemDragData(
  data: Record<string, unknown>
): data is Record<string, unknown> & {
  type: string;
  index: number;
  key: string | null;
} {
  return data.type === LINE_ITEM_DND_TYPE;
}
